import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { formatCurrency } from 'tio-ui/format';

export interface SummaryNodeRaw {
  level: number;
  label?: string;
  children?: string[];
  eligible?: boolean | null;
  db_limit?: number | null;
  db_annual_limit?: number | null;
  aggregate_limit?: number | null;
  aggregate_annual_limit?: number | null;
  effective_limit?: number | null;
  effective_annual_limit?: number | null;
  used?: number;
  annual_used?: number;
  /** External benefit spend attached directly to this node (node-local, NOT rolled up from children). */
  external_used?: number;
  external_annual_used?: number;
  remaining?: number | null;
  binding_limit?: number | null;
  binding_used?: number;
  binding_period?: 'annual' | 'lifetime';
  employment_type?: string | null;
  /** Company mode, shared nodes: each employment type's effective lifetime cap, cents. */
  effective_limit_by_employment_type?: Record<string, number | null>;
  /** Company mode, shared nodes: each employment type's effective annual cap, cents. */
  effective_annual_limit_by_employment_type?: Record<string, number | null>;
}

/** Company-root rollup mirrored at the top level of the summary payload. */
export interface SummaryBlock {
  db_limit?: number | null;
  db_annual_limit?: number | null;
  aggregate_limit?: number | null;
  aggregate_annual_limit?: number | null;
  effective_limit?: number | null;
  effective_annual_limit?: number | null;
  used?: number;
  annual_used?: number;
  remaining?: number | null;
}

export interface SummaryResponse {
  summary?: SummaryBlock;
  tree: Record<string, SummaryNodeRaw>;
  meta: {
    company_id: string;
    employee_id?: string;
    as_of_date: string;
    /** Fiscal window containing as_of_date; end date is exclusive and starts the next window. */
    annual_start_date?: string;
    annual_end_date?: string;
    /** Employee mode: whether usage counted paid items only (true) or reserved + paid (false, the default). */
    paid_only?: boolean;
    /** Employee mode: the balance multiplier applied to the company-level limit (1 when none). */
    balance_multiplier?: number;
    /** Employee mode: the employment type the tree was resolved for. */
    employment_type?: string | null;
  };
}

/**
 * Format cents as a whole-dollar string, or the given `unlimited` label when
 * null/undefined. The caller supplies the label so it can be translated.
 */
export const fmt = (cents: number | null | undefined, unlimited: string) =>
  cents == null ? unlimited : formatCurrency(cents / 100, 0);

/**
 * GET /api/v2/limits/summary — pass employeeId or companyId (not both).
 * `date` selects the fiscal window (defaults to today server-side); pass the
 * current response's `meta.annual_end_date` to fetch the next period.
 * `paidOnly` (employee mode) counts paid items only; omitted, usage counts
 * reserved + paid.
 */
export function fetchLimitSummaryRequest(
  params: ({ employeeId: string; paidOnly?: boolean } | { companyId: string; paidOnly?: never }) & {
    date?: string;
  }
): RequestInfo {
  const opts: QueryUrlOptions = {
    identifier: { type: 'limits' },
    op: 'query',
    resourcePath: 'api/v2/limits/summary',
  };
  const url = buildBaseURL(opts);
  const qs =
    'employeeId' in params
      ? `employee_id=${encodeURIComponent(params.employeeId)}`
      : `company_id=${encodeURIComponent(params.companyId)}`;
  const dateQs = params.date ? `&date=${encodeURIComponent(params.date)}` : '';
  const paidOnlyQs = params.paidOnly ? '&paid_only=true' : '';
  return {
    url: `${url}?${qs}${dateQs}${paidOnlyQs}`,
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}
